import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dbSurgeries, dbTreatmentPlans, type Surgery, type TreatmentPlan } from '../../lib/localDB';
import { buildInitialSteps, recalcPlannedDates, getStepStatus } from './types';
import TreatmentPlanCard from './components/TreatmentPlanCard';
import ClinicSettingsPanel from './components/ClinicSettingsPanel';

const SELECTED_CLINICS_KEY = 'appointments_selected_clinics';

type PatientPlans = {
  patientName: string;
  clinicName?: string;
  entries: { surgery: Surgery; plan: TreatmentPlan }[];
};

export default function AppointmentsPage() {
  const navigate = useNavigate();
  const [patientGroups, setPatientGroups] = useState<PatientPlans[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'completed' | 'overdue'>('all');
  const [expandedPatients, setExpandedPatients] = useState<Set<string>>(new Set());
  const [successMsg, setSuccessMsg] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [selectedClinics, setSelectedClinics] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(SELECTED_CLINICS_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const today = new Date().toISOString().split('T')[0];

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target as Node)) {
        setShowMobileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleClinicChange = (clinics: string[]) => {
    setSelectedClinics(clinics);
    localStorage.setItem(SELECTED_CLINICS_KEY, JSON.stringify(clinics));
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const surgeries = await dbSurgeries.getAll();
      const existingPlans = await dbTreatmentPlans.getAll();
      const planBySurgeryId = new Map(existingPlans.map((p) => [p.surgery_id, p]));

      // Auto-create missing plans
      const newPlans: TreatmentPlan[] = [];
      for (const surgery of surgeries) {
        if (!planBySurgeryId.has(surgery.id)) {
          const created = await dbTreatmentPlans.insert({
            surgery_id: surgery.id,
            patient_name: surgery.patient_name,
            operation_date: surgery.operation_date,
            steps: buildInitialSteps(surgery.operation_date),
            all_completed: false,
          });
          planBySurgeryId.set(surgery.id, created);
          newPlans.push(created);
        }
      }

      // Build patient groups
      const map = new Map<string, { surgery: Surgery; plan: TreatmentPlan; clinicName?: string }[]>();
      for (const surgery of surgeries) {
        const plan = planBySurgeryId.get(surgery.id);
        if (!plan) continue;
        const key = surgery.patient_name?.trim() || 'Adsız';
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push({ surgery, plan, clinicName: surgery.clinic });
      }

      const groups: PatientPlans[] = [];
      map.forEach((entries, patientName) => {
        entries.sort((a, b) =>
          new Date(b.surgery.operation_date).getTime() - new Date(a.surgery.operation_date).getTime()
        );
        groups.push({ patientName, clinicName: entries[0]?.clinicName, entries });
      });
      groups.sort((a, b) => {
        const aDate = a.entries[0]?.surgery.operation_date ?? '';
        const bDate = b.entries[0]?.surgery.operation_date ?? '';
        return new Date(bDate).getTime() - new Date(aDate).getTime();
      });

      setPatientGroups(groups);
      const names = new Set(groups.map((g) => g.patientName));
      setExpandedPatients(names);
    } catch (e) {
      console.error('Veri yüklenemedi:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStep = async (planId: string, stepId: string) => {
    try {
      const allPlans = await dbTreatmentPlans.getAll();
      const plan = allPlans.find((p) => p.id === planId);
      if (!plan) return;

      let steps = plan.steps.map((s) => {
        if (s.step_id !== stepId) return s;
        if (s.completed) {
          return { ...s, completed: false, completed_date: null };
        }
        return { ...s, completed: true, completed_date: today };
      });

      steps = recalcPlannedDates(steps);
      const allCompleted = steps.every((s) => s.completed);

      await dbTreatmentPlans.update(planId, { steps, all_completed: allCompleted });

      // Mark surgery as completed if all steps done
      await dbSurgeries.update(plan.surgery_id, { completed: allCompleted });

      if (allCompleted) {
        showSuccess('Tüm adımlar tamamlandı! Hasta tamamlandı olarak işaretlendi.');
      }

      await loadAll();
    } catch (e) {
      console.error('Adım güncelleme hatası:', e);
    }
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3500);
  };

  const togglePatient = (name: string) => {
    setExpandedPatients((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Filter by selected clinics first
  const clinicFilteredGroups = useMemo(() => {
    if (selectedClinics.length === 0) return [];
    return patientGroups.filter((group) =>
      group.entries.some((e) => {
        const clinic = e.surgery.clinic?.trim() ?? '';
        return selectedClinics.includes(clinic);
      })
    ).map((group) => ({
      ...group,
      entries: group.entries.filter((e) => {
        const clinic = e.surgery.clinic?.trim() ?? '';
        return selectedClinics.includes(clinic);
      }),
    }));
  }, [patientGroups, selectedClinics]);

  // Stats (based on clinic-filtered data)
  const stats = useMemo(() => {
    let totalPlans = 0;
    let completedPlans = 0;
    let overdueSteps = 0;
    let dueSoonSteps = 0;

    clinicFilteredGroups.forEach(({ entries }) => {
      entries.forEach(({ plan }) => {
        totalPlans++;
        if (plan.all_completed) completedPlans++;
        plan.steps.forEach((step) => {
          const s = getStepStatus(step, today);
          if (s === 'overdue') overdueSteps++;
          if (s === 'due_soon') dueSoonSteps++;
        });
      });
    });

    return { totalPlans, completedPlans, overdueSteps, dueSoonSteps, activePlans: totalPlans - completedPlans };
  }, [clinicFilteredGroups, today]);

  // Search + status filter
  const filteredGroups = useMemo(() => {
    return clinicFilteredGroups
      .map((group) => {
        const filteredEntries = group.entries.filter(({ plan, surgery }) => {
          if (searchQuery.trim()) {
            const q = searchQuery.trim().toLowerCase();
            const matchName = group.patientName.toLowerCase().includes(q);
            const matchDate = new Date(surgery.operation_date).toLocaleDateString('tr-TR').includes(q);
            const matchClinic = (surgery.clinic ?? '').toLowerCase().includes(q);
            if (!matchName && !matchDate && !matchClinic) return false;
          }
          if (filterStatus === 'completed' && !plan.all_completed) return false;
          if (filterStatus === 'active' && plan.all_completed) return false;
          if (filterStatus === 'overdue') {
            const hasOverdue = plan.steps.some((s) => getStepStatus(s, today) === 'overdue');
            if (!hasOverdue) return false;
          }
          return true;
        });
        return { ...group, entries: filteredEntries };
      })
      .filter((g) => g.entries.length > 0);
  }, [clinicFilteredGroups, searchQuery, filterStatus, today]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Tedavi planları yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* NAV */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <i className="ri-calendar-todo-line text-2xl text-white"></i>
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Tedavi Takibi</h1>
              <p className="text-xs text-gray-500">Randevu & Aşama Yönetimi</p>
            </div>
          </div>
          <div className="flex flex-1 items-center justify-end gap-3">
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-2">
              <button onClick={() => setSettingsOpen(true)} className="relative px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-settings-3-line text-lg"></i>
                Ayarlar
                {selectedClinics.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {selectedClinics.length}
                  </span>
                )}
              </button>
              <button onClick={() => navigate('/calendar')} className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-calendar-2-line text-lg"></i>Takvim
              </button>
              <button onClick={() => navigate('/dashboard')} className="px-4 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm">
                <i className="ri-layout-line text-lg"></i>Dashboard
              </button>
            </div>

            {/* Mobile Navigation Toggle */}
            <div className="md:hidden relative" ref={mobileMenuRef}>
              <button
                onClick={() => setShowMobileMenu((v) => !v)}
                className={`p-2 rounded-lg transition-all flex items-center justify-center border ${showMobileMenu ? 'bg-gray-100 border-gray-300 text-gray-900' : 'text-gray-700 hover:bg-gray-100 border-transparent'}`}
                title="Menü"
              >
                <i className={`${showMobileMenu ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
              
              {showMobileMenu && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl overflow-hidden z-50 flex flex-col" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                  <div className="py-2 flex flex-col">
                    <button onClick={() => { setSettingsOpen(true); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer flex items-center justify-between">
                      Ayarlar
                      {selectedClinics.length > 0 && (
                        <span className="w-5 h-5 bg-teal-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {selectedClinics.length}
                        </span>
                      )}
                    </button>
                    <button onClick={() => { navigate('/calendar'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Takvim
                    </button>
                    <button onClick={() => { navigate('/dashboard'); setShowMobileMenu(false); }} className="w-full px-5 py-3 text-left text-sm font-medium text-gray-700 hover:text-teal-700 hover:bg-teal-50 cursor-pointer">
                      Dashboard
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button onClick={() => navigate('/new-record')} className="px-3 py-2 md:px-5 md:py-2.5 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-700 transition-all flex items-center gap-1 md:gap-2 whitespace-nowrap cursor-pointer text-xs md:text-sm">
              <i className="ri-add-line text-base md:text-lg hidden md:block"></i>Yeni Kayıt
            </button>
          </div>
        </div>
      </nav>

      {/* Settings Panel */}
      <ClinicSettingsPanel
        open={settingsOpen}
        selectedClinics={selectedClinics}
        onClose={() => setSettingsOpen(false)}
        onChange={handleClinicChange}
      />

      {successMsg && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-green-500 text-white rounded-xl flex items-center gap-2 text-sm shadow-lg max-w-sm text-center">
          <i className="ri-check-double-line text-xl flex-shrink-0"></i>
          {successMsg}
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-10">

        {/* HEADER */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-2">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Tedavi Planları</h2>
              <p className="text-gray-500 text-sm mt-1">Her ameliyat için 6 aşamalı tedavi takibi. Adımları tamamladıkça işaretleyin.</p>
            </div>
            {/* Active clinic filter indicator */}
            {selectedClinics.length > 0 && (
              <div className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-xl px-4 py-2.5 ml-4 flex-shrink-0">
                <i className="ri-hospital-line text-teal-600 text-sm"></i>
                <div>
                  <p className="text-xs font-semibold text-teal-700">{selectedClinics.length} klinik filtreleniyor</p>
                  <p className="text-xs text-teal-600 truncate max-w-[180px]">{selectedClinics.slice(0, 2).join(', ')}{selectedClinics.length > 2 ? ` +${selectedClinics.length - 2} daha` : ''}</p>
                </div>
                <button
                  onClick={() => handleClinicChange([])}
                  className="ml-1 w-5 h-5 flex items-center justify-center rounded-full bg-teal-200 hover:bg-teal-300 cursor-pointer transition-all flex-shrink-0"
                >
                  <i className="ri-close-line text-teal-700 text-xs"></i>
                </button>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            {[
              { icon: 'ri-group-line', color: 'teal', label: 'Aktif Tedavi', value: stats.activePlans },
              { icon: 'ri-checkbox-circle-line', color: 'green', label: 'Tamamlandı', value: stats.completedPlans },
              { icon: 'ri-alarm-warning-line', color: 'red', label: 'Gecikmiş Adım', value: stats.overdueSteps },
              { icon: 'ri-time-line', color: 'amber', label: 'Bu Hafta', value: stats.dueSoonSteps },
            ].map((stat) => (
              <div key={stat.label} className="bg-white rounded-xl px-4 py-3 flex items-center gap-3">
                <div className={`w-10 h-10 bg-${stat.color}-50 rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <i className={`${stat.icon} text-${stat.color}-600 text-lg`}></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                  <p className="text-xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>


        {/* SEARCH & FILTER */}
        {clinicFilteredGroups.length > 0 && (
          <div className="mb-6 space-y-3">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Hasta adı, klinik veya tarih ile ara..."
                className="w-full pl-12 pr-12 py-4 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery('')} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer w-6 h-6 flex items-center justify-center">
                  <i className="ri-close-line text-lg"></i>
                </button>
              )}
            </div>

            <div className="bg-white rounded-xl px-4 py-3 flex flex-wrap items-center gap-3">
              <span className="text-xs font-medium text-gray-500">Durum:</span>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                {([
                  { val: 'all', label: 'Tümü' },
                  { val: 'active', label: 'Aktif' },
                  { val: 'overdue', label: 'Gecikmiş' },
                  { val: 'completed', label: 'Tamamlandı' },
                ] as const).map((opt) => (
                  <button
                    key={opt.val}
                    onClick={() => setFilterStatus(opt.val)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                      filterStatus === opt.val ? 'bg-teal-500 text-white' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* EMPTY STATE */}
        {patientGroups.length === 0 ? (
          <div className="bg-white rounded-2xl p-16 text-center">
            <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-calendar-todo-line text-4xl text-teal-400"></i>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Henüz kayıt yok</h3>
            <p className="text-gray-500 mb-6 text-sm">İlk ameliyat kaydını oluşturunca tedavi planı otomatik oluşacak.</p>
            <button
              onClick={() => navigate('/new-record')}
              className="px-4 py-2 md:px-6 md:py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-700 transition-all inline-flex items-center gap-1 md:gap-2 whitespace-nowrap cursor-pointer text-sm md:text-base"
            >
              <i className="ri-add-line text-base md:text-lg hidden md:block"></i>Yeni Kayıt Oluştur
            </button>
          </div>
        ) : clinicFilteredGroups.length === 0 ? (
          <div className="bg-white rounded-2xl p-14 text-center">
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-hospital-line text-3xl text-teal-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {selectedClinics.length === 0 ? 'Klinik seçilmedi' : 'Seçili kliniklere ait hasta bulunamadı'}
            </h3>
            <p className="text-gray-500 text-sm mb-5">
              {selectedClinics.length === 0
                ? 'Tedavi takibi görmek için ayarlardan klinik seçin.'
                : 'Seçtiğiniz kliniklerin hastalarına ait kayıt yok ya da hasta kayıtlarında klinik bilgisi eksik.'}
            </p>
            <button
              onClick={() => setSettingsOpen(true)}
              className="px-5 py-2.5 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-all whitespace-nowrap cursor-pointer inline-flex items-center gap-2"
            >
              <i className="ri-settings-3-line"></i>{selectedClinics.length === 0 ? 'Klinik Seç' : 'Klinik Seçimini Değiştir'}
            </button>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Sonuç bulunamadı</h3>
            <button
              onClick={() => { setSearchQuery(''); setFilterStatus('all'); }}
              className="px-5 py-2.5 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-all whitespace-nowrap cursor-pointer"
            >
              Filtreleri Temizle
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredGroups.map((group) => {
              const isExpanded = expandedPatients.has(group.patientName);
              const allDone = group.entries.every((e) => e.plan.all_completed);
              const overdueInGroup = group.entries.some((e) =>
                e.plan.steps.some((s) => getStepStatus(s, today) === 'overdue')
              );

              return (
                <div key={group.patientName} className="bg-white rounded-2xl overflow-hidden">
                  {/* Patient header */}
                  <div
                    onClick={() => togglePatient(group.patientName)}
                    className="px-6 py-5 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg ${allDone ? 'bg-green-500' : 'bg-gradient-to-br from-teal-500 to-cyan-600'}`}>
                        {allDone
                          ? <i className="ri-shield-check-line text-xl"></i>
                          : group.patientName.slice(0, 1).toUpperCase()
                        }
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="text-lg font-bold text-gray-900">{group.patientName}</h3>
                          {group.clinicName && (
                            <span className="px-2 py-0.5 bg-teal-50 text-teal-700 rounded-full text-xs font-medium flex items-center gap-1">
                              <i className="ri-hospital-line text-xs"></i>{group.clinicName}
                            </span>
                          )}
                          {allDone && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-bold flex items-center gap-1">
                              <i className="ri-check-double-line"></i>Tamamlandı
                            </span>
                          )}
                          {!allDone && overdueInGroup && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-bold flex items-center gap-1">
                              <i className="ri-alarm-warning-line"></i>Gecikmiş Adım
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {group.entries.length} ameliyat · {group.entries.filter((e) => e.plan.all_completed).length} tedavi tamamlandı
                        </p>
                      </div>
                    </div>
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-xl text-gray-400"></i>
                    </div>
                  </div>

                  {/* Plans */}
                  {isExpanded && (
                    <div className="px-6 pb-6 space-y-4">
                      {group.entries.map(({ surgery, plan }) => (
                        <TreatmentPlanCard
                          key={plan.id}
                          plan={plan}
                          onToggleStep={handleToggleStep}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
